import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ConfiguracionSecuenciaModal = ({ isOpen, onClose, onSaved }) => {
    const [config, setConfig] = useState({
        current_number: 1,
        initial_number: 1,
        auto_reset_daily: true,
        last_reset_date: null,
        updated_at: null
    });
    const [nextNumberInput, setNextNumberInput] = useState('1');
    const [initialNumberInput, setInitialNumberInput] = useState('1');
    const [autoResetDaily, setAutoResetDaily] = useState(true);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const fetchConfig = async () => {
        try {
            setLoading(true);
            setErrorMessage('');
            const res = await api.get('/api/orders/orders/sequence_config/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            if (res.data) {
                setConfig(res.data);
                setNextNumberInput(String(res.data.current_number ?? 1));
                setInitialNumberInput(String(res.data.initial_number ?? 1));
                setAutoResetDaily(res.data.auto_reset_daily !== false);
            }
        } catch (err) {
            console.error('Error fetching sequence config:', err);
            setErrorMessage('No se pudo cargar la configuración de secuencia.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchConfig();
            setSuccessMessage('');
            setErrorMessage('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSaveSettings = async (e) => {
        if (e) e.preventDefault();
        const num = parseInt(nextNumberInput, 10);
        const initNum = parseInt(initialNumberInput, 10);

        if (isNaN(num) || num < 1) {
            setErrorMessage('El siguiente número debe ser mayor o igual a 1.');
            return;
        }
        if (isNaN(initNum) || initNum < 1) {
            setErrorMessage('El número inicial debe ser mayor o igual a 1.');
            return;
        }

        setSaving(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const res = await api.post(
                '/api/orders/orders/sequence_config/',
                {
                    current_number: num,
                    initial_number: initNum,
                    auto_reset_daily: autoResetDaily
                },
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            setConfig(res.data);
            setSuccessMessage(`Configuración guardada. Próxima orden: #${res.data.current_number}`);
            if (onSaved) onSaved(res.data);
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (err) {
            console.error('Error saving sequence config:', err);
            setErrorMessage('Error al guardar la configuración.');
        } finally {
            setSaving(false);
        }
    };

    const handleResetNow = async () => {
        const initNum = parseInt(initialNumberInput, 10) || 1;
        const confirmReset = window.confirm(
            `¿Estás seguro de reiniciar el número de orden a #${initNum} en este momento?`
        );
        if (!confirmReset) return;

        setSaving(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const res = await api.post(
                '/api/orders/orders/sequence_config/',
                {
                    reset_now: true,
                    initial_number: initNum,
                    auto_reset_daily: autoResetDaily
                },
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            setConfig(res.data);
            setNextNumberInput(String(res.data.current_number));
            setSuccessMessage(`¡Secuencia reiniciada a #${res.data.current_number} exitosamente!`);
            if (onSaved) onSaved(res.data);
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (err) {
            console.error('Error resetting sequence config:', err);
            setErrorMessage('Error al reiniciar la secuencia de órdenes.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.65)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1200,
                padding: '16px'
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    maxWidth: '480px',
                    width: '100%',
                    padding: '24px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    border: '1px solid #e2e8f0',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            backgroundColor: '#dcfce7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                            color: '#166534'
                        }}>
                            <i className="bi bi-123"></i>
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                                Número de Orden Diario
                            </h3>
                            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                                Comandas y Notas de Entrega
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '22px',
                            cursor: 'pointer',
                            color: '#94a3b8',
                            lineHeight: 1,
                            padding: '4px'
                        }}
                    >
                        &times;
                    </button>
                </div>

                {/* Status Card */}
                <div style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center'
                }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Próximo Número a Imprimir
                    </span>
                    <div style={{
                        fontSize: '42px',
                        fontWeight: '800',
                        color: '#15803d',
                        margin: '4px 0 8px 0',
                        letterSpacing: '-1px'
                    }}>
                        #{loading ? '...' : config.current_number}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', fontSize: '12px' }}>
                        <span style={{
                            backgroundColor: config.auto_reset_daily ? '#dcfce7' : '#fee2e2',
                            color: config.auto_reset_daily ? '#166534' : '#991b1b',
                            padding: '3px 10px',
                            borderRadius: '999px',
                            fontWeight: '600'
                        }}>
                            {config.auto_reset_daily ? 'Auto-reinicio a las 00:00 (Activo)' : 'Auto-reinicio Desactivado'}
                        </span>
                        {config.last_reset_date && (
                            <span style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '3px 10px', borderRadius: '999px' }}>
                                Último reinicio: {config.last_reset_date}
                            </span>
                        )}
                    </div>
                </div>

                {/* Messages */}
                {successMessage && (
                    <div style={{
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #86efac',
                        color: '#166534',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '500',
                        marginBottom: '16px'
                    }}>
                        {successMessage}
                    </div>
                )}

                {errorMessage && (
                    <div style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fca5a5',
                        color: '#991b1b',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '500',
                        marginBottom: '16px'
                    }}>
                        {errorMessage}
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSaveSettings}>
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                            Establecer Siguiente Número Manualmente:
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: '700' }}>#</span>
                                <input
                                    type="number"
                                    min="1"
                                    value={nextNumberInput}
                                    onChange={(e) => setNextNumberInput(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px 10px 28px',
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        color: '#0f172a'
                                    }}
                                    required
                                />
                            </div>
                        </div>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                            La siguiente venta o comanda tomará este número correlativo.
                        </span>
                    </div>

                    {/* Auto Reset Switch */}
                    <div style={{
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        padding: '12px 14px',
                        marginBottom: '16px'
                    }}>
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: '8px' }}>
                            <div>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
                                    Reiniciar automáticamente a las 00:00 (Medianoche)
                                </div>
                                <div style={{ fontSize: '11px', color: '#64748b' }}>
                                    Cada nuevo día, el contador volverá automáticamente a empezar desde el número inicial.
                                </div>
                            </div>
                            <input
                                type="checkbox"
                                checked={autoResetDaily}
                                onChange={(e) => setAutoResetDaily(e.target.checked)}
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer',
                                    accentColor: '#16a34a'
                                }}
                            />
                        </label>

                        {autoResetDaily && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #e2e8f0' }}>
                                <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>
                                    Número inicial de cada día:
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={initialNumberInput}
                                    onChange={(e) => setInitialNumberInput(e.target.value)}
                                    style={{
                                        width: '60px',
                                        padding: '4px 8px',
                                        fontSize: '13px',
                                        fontWeight: '700',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        textAlign: 'center'
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Quick Reset Action Button */}
                    <div style={{ marginBottom: '20px' }}>
                        <button
                            type="button"
                            onClick={handleResetNow}
                            disabled={saving}
                            style={{
                                width: '100%',
                                padding: '10px 14px',
                                backgroundColor: '#fef3c7',
                                color: '#92400e',
                                border: '1px solid #fde68a',
                                borderRadius: '8px',
                                fontWeight: '600',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                transition: 'background-color 0.2s'
                            }}
                        >
                            <i className="bi bi-arrow-repeat"></i> Reiniciar a #{initialNumberInput || 1} Ahora
                        </button>
                    </div>

                    {/* Footer Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                padding: '10px 18px',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                backgroundColor: '#ffffff',
                                color: '#475569',
                                fontWeight: '600',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        >
                            Cerrar
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: '#16a34a',
                                color: '#ffffff',
                                fontWeight: '600',
                                fontSize: '14px',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                opacity: saving ? 0.7 : 1,
                                boxShadow: '0 2px 4px rgba(22, 163, 74, 0.2)'
                            }}
                        >
                            {saving ? 'Guardando...' : 'Guardar Configuración'}
                        </button>
                    </div>
                </form>

                {/* Informative Note */}
                <div style={{
                    marginTop: '16px',
                    padding: '10px 12px',
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '8px',
                    fontSize: '11px',
                    color: '#1e40af',
                    lineHeight: '1.4'
                }}>
                    <strong> ¿Dónde se imprime este número?</strong>
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                        <li><strong>Comandas de cocina:</strong> Cabecera destacada <code>ORDEN #1</code></li>
                        <li><strong>Notas de entrega / Tickets:</strong> Cabecera <code>ORDEN #1</code></li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ConfiguracionSecuenciaModal;
